import {Checkbox, ListItemText, MenuItem, TextField} from "@mui/material";
import {useContext, useState} from "react";
import {DatePicker} from "@mui/x-date-pickers";
import {useTranslation} from "react-i18next";
import {useRecoilValue, useSetRecoilState} from "recoil";
import * as Yup from "yup";
import AdvancedFilter from "../../../component/AdvancedFilter";
import {addFilter, getFilterValue, renderMultiSelect} from "../../../utils/FilterUtils";
import {
    ID_FILTER_DEALERS,
    ID_FILTER_LEGAL_ENTITIES,
    ID_FILTER_PRINTOUT_CREATIONDATEFROM,
    ID_FILTER_PRINTOUT_CREATIONDATETO, ID_FILTER_PRINTOUT_INVOICE_NUMBER,
    ID_FILTER_PRINTOUT_MSCODE,
    ID_FILTER_PRINTOUT_MSNAME,
    ID_FILTER_PRINTOUT_REPORTNAME, ID_FILTER_PRINTOUT_SAP_INVOICE_NUMBER,
    ID_FILTER_PRINTOUT_SENTDATEFROM,
    ID_FILTER_PRINTOUT_SENTDATETO,
    ID_FILTER_PRINTOUT_STATUS,
    ID_FILTER_PRINTOUT_TYPE,
    ID_FILTER_SEARCH,
    msPrintoutDefaultValues
} from "../constants/MobilityStoreFilters";
import AsyncAutocomplete from "../../../component/AsyncAutocomplete";
import ColouredDot from "../../../component/ColouredDot";
import {
    ID_ACCESSORIES,
    ID_BE_FREE_EVO,
    ID_CAR_BOX,
    ID_CAR_CLOUD,
    ID_COMMISSIONS,
    ID_DAMAGE,
    ID_DAMAGE_SHEET,
    ID_FLEX_RENT,
    ID_FUEL,
    ID_TRANSFERS,
    ID_VOUCHERS
} from "../constants/PrintoutTypes";
import {
    ID_BILLED,
    ID_ERROR,
    ID_PROCESSING,
    ID_PUBLISHED,
    ID_SENT,
    ID_TO_SEND
} from "../constants/MobilityStoreStatuses";
import {COLOR_BILLED, COLOR_ERROR, COLOR_PUBLISHED, COLOR_SENT, COLOR_TO_SEND} from "../constants/MobilityStoreColors";
import {STATE_PRINTOUT_ACTIVE_FILTERS} from "../constants/MobilityStoreRecoil";
import {asEndOfDay, asStartOfDay} from "../../../utils/FinanceDateUtils";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {MobilityStore} from "../constants/MobilityStoreModels";
import * as React from "react";

function PrintoutsFilter() {

    const { t } = useTranslation('finance', { keyPrefix: 'mobility-store' });
    const { t: tg } = useTranslation("translation");

   // const { getStoresCodes, getStoresNames } = useMobilityStoreRepository();
    const filterDefs = useRecoilValue(STATE_PRINTOUT_ACTIVE_FILTERS);
    const setFilters = useSetRecoilState(STATE_PRINTOUT_ACTIVE_FILTERS);
    const [search, setSearch] = useState("");

    const {
        globalData: { user },
    } = useContext(GlobalContext);

     const mobilityStores: MobilityStore[] = (user.mobilityStores || [])
            .filter((dms: any)=>    dms.representativeInfo && 
                                    dms.financialInfo?.feesDued
                                )
            .map((dms: any) => {
            return {
                id: dms.id,
                tenant: dms.tenant,
                name: dms.name.toUpperCase(),
                code: dms.identificationCode.toUpperCase(),
                type: dms.type,
                sapId: dms.sapId,
                msVendorId: dms.representativeInfo.idSapRepresentative,
                msVendorName: dms.representativeInfo.companyName || dms.representativeInfo.nameAndSurnameOfTheContactPerson,
                msVendorEmail: dms.representativeInfo.emailOfRepresentative
            }
        })


    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));
    
    const [legalEntities, setLegalEntities] = useState<string[]>(legalEntityOptions.map((x: any) => x.value));

    const [dealers, setDealers] = useState<any[]>(dealerOptions);


    const getStoresCodes = (search: string) =>  Promise.resolve(mobilityStores
        .filter(x => (legalEntities).includes(x.tenant) && 
                x.code.includes(search.toUpperCase()))
                .map(x => x.code));


    const getStoresNames = (search: string) =>  Promise.resolve(mobilityStores
        .filter(x => (legalEntities).includes(x.tenant) && 
                x.name.includes(search.toUpperCase()))
                .map(x => x.name));

    const schema = Yup.object().shape({
        code: Yup.string().nullable(),
        name: Yup.string().nullable(),
        type: Yup.string(),
        statuses: Yup.array(),
        creationDateFrom: Yup.date().nullable().test("testFrom", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.creationDateTo || (value <= context.parent.creationDateTo);
        }).typeError(t('filters.errors.invalid-date')),
        creationDateTo: Yup.date().nullable().test("testTo", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.creationDateFrom || (value >= context.parent.creationDateFrom);
        }).typeError(t('filters.errors.invalid-date')),
        sentDateFrom: Yup.date().nullable().test("testFrom", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.sentDateTo || (value <= context.parent.sentDateTo);
        }).typeError(t('filters.errors.invalid-date')),
        sentDateTo: Yup.date().nullable().test("testTo", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.sentDateFrom || (value >= context.parent.sentDateFrom);
        }).typeError(t('filters.errors.invalid-date')),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne'))
    });

    const defaultValues = {
        code: getFilterValue(ID_FILTER_PRINTOUT_MSCODE, filterDefs) || "",
        name: getFilterValue(ID_FILTER_PRINTOUT_MSNAME, filterDefs) || "",
        type: getFilterValue(ID_FILTER_PRINTOUT_TYPE, filterDefs) || "",
        reportName: getFilterValue(ID_FILTER_PRINTOUT_REPORTNAME, filterDefs) || "",
        statuses: getFilterValue(ID_FILTER_PRINTOUT_STATUS, filterDefs) || [],
        creationDateFrom: getFilterValue(ID_FILTER_PRINTOUT_CREATIONDATEFROM, filterDefs) || null,
        creationDateTo: getFilterValue(ID_FILTER_PRINTOUT_CREATIONDATETO, filterDefs) || null,
        sentDateFrom: getFilterValue(ID_FILTER_PRINTOUT_SENTDATEFROM, filterDefs) || null,
        sentDateTo: getFilterValue(ID_FILTER_PRINTOUT_SENTDATETO, filterDefs) || null,
        legalEntities: getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) || legalEntityOptions.map((x: any) => x.value),
        dealers: getFilterValue(ID_FILTER_DEALERS, filterDefs) || [],
        invoiceNumber: getFilterValue(ID_FILTER_PRINTOUT_INVOICE_NUMBER, filterDefs) || "",
        sapInvoiceNumber: getFilterValue(ID_FILTER_PRINTOUT_SAP_INVOICE_NUMBER, filterDefs) || ""
    };

    const renderMSCode = (field: any) => (
        <AsyncAutocomplete
            id={"code"}
            callback={getStoresCodes}
            value={field.value}
            onChange={field.onChange}
            renderInput={(params) => <TextField {...params} label={t('filters.printouts.' + ID_FILTER_PRINTOUT_MSCODE)}/>}
        />
    );

    const renderMSName = (field: any) => (
            <AsyncAutocomplete
                    id={"name"}
                    callback={getStoresNames}
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField
                            {...params}
                            label={t('filters.printouts.' + ID_FILTER_PRINTOUT_MSNAME)}
                    />}
            />
    );

    const renderType = (field: any) => (
            <TextField fullWidth
                       select
                       label={t('filters.printouts.' + ID_FILTER_PRINTOUT_TYPE)}
                       value={field.value}
                       onChange={field.onChange}
            >
                <MenuItem value={""}>
                    <em/>
                </MenuItem>
                {types.map((option: any) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                ))}
            </TextField>
    );

    const renderStatuses = (field: any) => (
            <TextField fullWidth
                       label={t('filters.printouts.' + ID_FILTER_PRINTOUT_STATUS)}
                       select
                       SelectProps={{
                           multiple: true,
                           renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
                       }}
                       value={field.value}
                       onChange={field.onChange}
            >
                {statuses.map((status) =>
                        <MenuItem key={status.value} value={status.value}>
                            <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                            <ColouredDot color={status?.color}/>
                            <ListItemText primary={status.label}/>
                        </MenuItem>
                )}
            </TextField>
    );

    const renderCreationDateFrom = (field: any, error: any, state: any, values: any) => (
            <DatePicker label={t('filters.printouts.' + ID_FILTER_PRINTOUT_CREATIONDATEFROM)}
                        views={['day']}
                        maxDate={values['creationDateTo'] !== null ? values['creationDateTo'] : null}
                        value={field.value}
                        onChange={field.onChange}
                        renderInput={(params) => <TextField {...params} fullWidth
                                                            id={"creationDateFrom"}
                                                            error={error !== undefined}
                                                            helperText={error ? error.message : ""}
                        />}
                        inputFormat={"L"}
            />
    );

    const renderCreationDateTo = (field: any, error: any, state: any, values: any) => (
            <DatePicker label={t('filters.printouts.' + ID_FILTER_PRINTOUT_CREATIONDATETO)}
                        views={['day']}
                        minDate={values['creationDateFrom'] !== null ? values['creationDateFrom'] : null}
                        value={field.value}
                        onChange={field.onChange}
                        renderInput={(params) => <TextField {...params} fullWidth
                                                            id={"creationDateTo"}
                                                            error={error !== undefined}
                                                            helperText={error ? error.message : ""}
                        />}
                        inputFormat={"L"}
            />
    );

    const renderSentDateFrom = (field: any, error: any, state: any, values: any) => (
            <DatePicker label={t('filters.printouts.published-date-from')}
                        views={['day']}
                        maxDate={values['sentDateTo'] !== null ? values['sentDateTo'] : null}
                        value={field.value}
                        onChange={field.onChange}
                        renderInput={(params) => <TextField {...params} fullWidth
                                                            id={"sentDateFrom"}
                                                            error={error !== undefined}
                                                            helperText={error ? error.message : ""}
                        />}
                        inputFormat={"L"}
            />
    );

    const renderSentDateTo = (field: any, error: any, state: any, values: any) => (
            <DatePicker label={t('filters.printouts.published-date-to')}
                        views={['day']}
                        minDate={values['sentDateFrom'] !== null ? values['sentDateFrom'] : null}
                        value={field.value}
                        onChange={field.onChange}
                        renderInput={(params) => <TextField {...params} fullWidth
                                                            id={"sentDateTo"}
                                                            error={error !== undefined}
                                                            helperText={error ? error.message : ""}
                        />}
                        inputFormat={"L"}
            />
    );

    const renderReportName = (field: any) => (
        <TextField
            fullWidth
            name="reportName"
            label={t("filters.printouts.report-name")}
            value={field.value}
            onChange={field.onChange}
        ></TextField>
    );

    const renderInvoiceNumber = (field: any) => (
        <TextField
            fullWidth
            name="invoice-number"
            label={t('filters.single-printouts.' + ID_FILTER_PRINTOUT_INVOICE_NUMBER)}
            value={field.value}
            onChange={field.onChange}
        ></TextField>
    );

    const renderSapInvoiceNumber = (field: any) => (
        <TextField
            fullWidth
            name="sap-invoice-number"
            label={t('filters.single-printouts.' + ID_FILTER_PRINTOUT_SAP_INVOICE_NUMBER)}
            value={field.value}
            onChange={field.onChange}
        ></TextField>
    );

    const fields = {
        code: renderMSCode,
        name: renderMSName,
        type: renderType,
        statuses: renderStatuses,
        creationDateFrom: renderCreationDateFrom,
        creationDateTo: renderCreationDateTo,
        sentDateFrom: renderSentDateFrom,
        sentDateTo: renderSentDateTo,
        reportName: renderReportName,
        invoiceNumber: renderInvoiceNumber,
        sapInvoiceNumber: renderSapInvoiceNumber,
        legalEntities: renderMultiSelect(
              tg("shared.legalEntities"),
              legalEntityOptions,
              "legalEntities",
              legalEntityOptions.length <= 1,
              (value: any, setValue: any) => {
                setValue('dealers', []);
                setLegalEntities(value);
                let dealers = user?.dealers
                  ?.filter(
                    (x: any) =>
                      value?.length > 0 &&
                      x.legalEntities.some((e: any) => value.includes(e.name)) &&
                      x.name.toLowerCase() !== "all"
                  )
                  .map((x: any) => {
                    return {
                      label: tg(`dealers.${x.name}`),
                      value: x.name,
                    };
                  });
                setDealers(dealers);
              }
            ),
            dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1),
    }

    const types = [
        {
            value: ID_ACCESSORIES,
            label: t('printout-types.' + ID_ACCESSORIES)
        },
        {
            value: ID_FUEL,
            label: t('printout-types.' + ID_FUEL)
        },
        {
            value: ID_TRANSFERS,
            label: t('printout-types.' + ID_TRANSFERS)
        },
        {
            value: ID_COMMISSIONS,
            label: t('printout-types.' + ID_COMMISSIONS)
        },
        {
            value: ID_DAMAGE,
            label: t('printout-types.' + ID_DAMAGE)
        },
        {
            value: ID_VOUCHERS,
            label: t('printout-types.' + ID_VOUCHERS)
        },
        {
            value: ID_BE_FREE_EVO,
            label: t('printout-types.' + ID_BE_FREE_EVO)
        },
        {
            value: ID_CAR_CLOUD,
            label: t('printout-types.' + ID_CAR_CLOUD)
        },
        {
            value: ID_FLEX_RENT,
            label: t('printout-types.' + ID_FLEX_RENT)
        },
        {
            value: ID_CAR_BOX,
            label: t('printout-types.' + ID_CAR_BOX)
        },
        {
            value: ID_DAMAGE_SHEET,
            label: t('printout-types.' + ID_DAMAGE_SHEET)
        }
    ];

    const statuses = [
        {
            value: ID_PUBLISHED,
            label: t('statuses.' + ID_PUBLISHED),
            color: COLOR_PUBLISHED + '.transparent'
        },
        {
            value: ID_TO_SEND,
            label: t('statuses.' + ID_TO_SEND),
            color: COLOR_TO_SEND + '.transparent'
        },
        {
            value: ID_BILLED,
            label: t('statuses.' + ID_BILLED),
            color: COLOR_BILLED + '.transparent'
        },
        {
            value: ID_SENT,
            label: t('statuses.' + ID_SENT),
            color: COLOR_SENT + '.transparent'
        },
        {
            value: ID_PROCESSING,
            label: t('statuses.' + ID_PROCESSING),
        },
        {
            value: ID_ERROR,
            label: t('statuses.' + ID_ERROR),
            color: COLOR_ERROR + '.transparent'
        },
    ];

    const submit = (data: any) => {
        addFilter(setFilters, ID_FILTER_SEARCH, search);
        addFilter(setFilters, ID_FILTER_PRINTOUT_MSNAME, data.name);
        addFilter(setFilters, ID_FILTER_PRINTOUT_MSCODE, data.code);
        addFilter(setFilters, ID_FILTER_PRINTOUT_TYPE, data.type);
        addFilter(setFilters, ID_FILTER_PRINTOUT_REPORTNAME, data.reportName);
        addFilter(setFilters, ID_FILTER_PRINTOUT_CREATIONDATEFROM, asStartOfDay(data.creationDateFrom));
        addFilter(setFilters, ID_FILTER_PRINTOUT_CREATIONDATETO, asEndOfDay(data.creationDateTo));
        addFilter(setFilters, ID_FILTER_PRINTOUT_SENTDATEFROM, asStartOfDay(data.sentDateFrom));
        addFilter(setFilters, ID_FILTER_PRINTOUT_SENTDATETO, asEndOfDay(data.sentDateTo));
        addFilter(setFilters, ID_FILTER_PRINTOUT_STATUS, data.statuses);
        addFilter(setFilters, ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilters, ID_FILTER_DEALERS, data.dealers);
        addFilter(setFilters, ID_FILTER_PRINTOUT_INVOICE_NUMBER, data.invoiceNumber);
        addFilter(setFilters, ID_FILTER_PRINTOUT_SAP_INVOICE_NUMBER, data.sapInvoiceNumber);
    }

    const reset = () => {
        setFilters([]);
    }

    return (
            <AdvancedFilter schema={schema} fields={fields} defaultValues={defaultValues}
                            searchInput={search} setSearchInput={setSearch}
                            customDefaultValues={msPrintoutDefaultValues}
                            onSubmit={submit}
                            onReset={reset}
            />
    )
}

export default PrintoutsFilter;